import {
  COLOR_OPTIONS,
  type DesignTemplate,
} from '../../../../constants/designTemplates';
import { getShopParityButtonStyle } from '../../../../Utils/StayUpToDate/RenderComponent';

const FONT_FALLBACK = 'Arial, sans-serif';

export function profileUserToDesignRow(profileUser: any | undefined) {
  if (!profileUser) return undefined;
  const dt = profileUser.designTokens;
  return {
    template_name:
      profileUser.template_name ?? dt?.templateName ?? dt?.template_name,
    font_family:
      profileUser.font_family ?? dt?.font_family ?? dt?.fontFamily ?? FONT_FALLBACK,
    background_style:
      profileUser.background_style ??
      dt?.background_style ??
      dt?.primaryColor ??
      '',
    button_background:
      profileUser.button_background ??
      dt?.button_background ??
      dt?.secondaryColor ??
      '',
    button_text:
      profileUser.button_text ?? dt?.button_text ?? dt?.tertiaryColor ?? '',
    button_border: profileUser.button_border ?? dt?.button_border ?? '',
    text_color: profileUser.text_color ?? dt?.text_color ?? '',
    social_icon_color:
      profileUser.social_icon_color ?? dt?.social_icon_color ?? '',
    card_style:
      profileUser.card_style ??
      dt?.card_style ??
      dt?.forthColor,
  };
}

function resolvePresetIndex(row: any): number {
  let foundIndex = COLOR_OPTIONS.findIndex(
    (color) => color.name === row?.template_name
  );
  if (foundIndex === -1 && row) {
    foundIndex = COLOR_OPTIONS.findIndex(
      (color) =>
        color.background === row.background_style &&
        color.button.background === row.button_background &&
        color.button.text === row.button_text &&
        color.text === row.text_color &&
        color.socialIcon === row.social_icon_color
    );
  }
  if (foundIndex === -1 && row?.template_name) {
    const legacyMappings: Record<string, string> = {
      'Basic Grayscale': 'Default',
      Basic: 'Default',
    };
    const mappedName = legacyMappings[row.template_name];
    if (mappedName) {
      foundIndex = COLOR_OPTIONS.findIndex((color) => color.name === mappedName);
    }
  }
  return foundIndex;
}

export function buildDesignStateFromProfileRow(row: any | undefined): {
  template: DesignTemplate;
  fontFamily: string;
} {
  const presetIdx = row ? resolvePresetIndex(row) : -1;
  const hasColors =
    row?.background_style && row?.button_background && row?.button_text;

  const isCustomName = row?.template_name === 'Custom';
  const treatAsCustom =
    (isCustomName && hasColors) || (presetIdx === -1 && Boolean(hasColors));

  let template: DesignTemplate;

  if (treatAsCustom && hasColors) {
    template = {
      name: 'Custom',
      background: row.background_style,
      button: {
        background: row.button_background,
        text: row.button_text,
        border: row.button_border || '',
      },
      text: row.text_color || '#000000',
      socialIcon: row.social_icon_color || row.text_color || '#000000',
      card_style: row.card_style,
    };
  } else if (hasColors) {
    const idx = presetIdx !== -1 ? presetIdx : 0;
    template = {
      name: row.template_name || COLOR_OPTIONS[idx].name,
      background: row.background_style,
      button: {
        background: row.button_background,
        text: row.button_text,
        border: row.button_border || '',
      },
      text: row.text_color || COLOR_OPTIONS[idx].text,
      socialIcon: row.social_icon_color || COLOR_OPTIONS[idx].socialIcon,
      card_style: row.card_style || COLOR_OPTIONS[idx].card_style,
    };
  } else {
    const idx = presetIdx !== -1 ? presetIdx : 0;
    template = COLOR_OPTIONS[idx];
  }

  return {
    template,
    fontFamily: row?.font_family || FONT_FALLBACK,
  };
}

export interface StoreProductPreviewTokens {
  cardBackground: string;
  cardBorder: string;
  fullViewBackground: string;
  fullViewBorder: string;
  textColor: string;
  mutedTextColor: string;
  buttonBackground: string;
  buttonText: string;
  priceColor: string;
  inputBackground: string;
  inputTextColor: string;
}

export interface StoreProductCardDesignTokens {
  templateName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  forthColor?: string;
  fontFamily?: string;
  primaryFontFamily?: string;
  secondaryFontFamily?: string;
}

export function templateToStoreDesignTokens(
  template: DesignTemplate,
  fontFamily: string = FONT_FALLBACK
): StoreProductCardDesignTokens & { fontFamily: string } {
  return {
    templateName: template.name,
    primaryColor: template.background,
    secondaryColor: template.button?.background,
    tertiaryColor: template.button?.text || template.text,
    forthColor: template.card_style,
    fontFamily,
  };
}

export function getStoreProductCardThemeStyle(
  designTokens?: StoreProductCardDesignTokens,
  brandColor?: string
): Record<string, string> {
  const templateName = String(designTokens?.templateName || '').toLowerCase();
  const isDefaultTheme = templateName === 'default';
  const shopParity = getShopParityButtonStyle({
    designTokens,
    brand_color: brandColor,
    // Same fill source as ProductShopButton / .shop-link (forthColor).
    contextButtonBackground: designTokens?.forthColor,
  });

  const cardBackground = isDefaultTheme
    ? '#ffffff'
    : 'transparent';

  // Match Banner + Highlight: border uses tertiaryColor (design button text / accent border)
  const cardBorder = isDefaultTheme
    ? '#e6e7eb'
    : designTokens?.tertiaryColor || brandColor || '#e6e7eb';

  // Match .creator-theme p / highlight-name / shop button label: secondaryColor
  // is the content text accent. Title + subtitle must share this so public page,
  // design preview, and dashboard stay in sync (tertiary is border/button-text).
  const contentTextColor = isDefaultTheme
    ? '#111827'
    : designTokens?.secondaryColor || designTokens?.tertiaryColor || '#ffffff';

  const textColor = contentTextColor;

  const mutedTextColor = isDefaultTheme ? '#6b7280' : contentTextColor;

  const fullViewBorder = cardBorder;

  return {
    '--store-preview-card-bg': cardBackground,
    '--store-preview-card-border': cardBorder,
    '--store-preview-full-view-bg': isDefaultTheme ? '#ffffff' : 'transparent',
    '--store-preview-full-view-border': fullViewBorder,
    '--store-preview-text': textColor,
    '--store-preview-muted-text': mutedTextColor,
    '--store-preview-btn-bg': shopParity.backgroundColor,
    '--store-preview-btn-text': shopParity.color,
    // Hover parity with the regular Shop button (ProductShopButton).
    '--hover-bg': shopParity.hoverBg,
    '--follower-cta-fg': shopParity.color,
    '--store-preview-media-bg': isDefaultTheme
      ? '#e5e7eb'
      : 'rgba(255, 255, 255, 0.18)',
  };
}

export function getStoreProductPreviewTokens(
  template: DesignTemplate
): StoreProductPreviewTokens {
  const isDefault = (template.name || '').toLowerCase() === 'default';
  const textColor = template.text || '#111827';
  // Same accent as getStoreProductCardThemeStyle / shop button label text.
  const contentTextColor = isDefault
    ? textColor
    : template.button?.background || template.text || textColor;
  const shopParity = getShopParityButtonStyle({
    designTokens: {
      templateName: template.name,
      secondaryColor: template.button?.background,
      tertiaryColor: template.button?.text || template.text,
      forthColor: template.card_style,
    },
    // Same fill as ProductShopButton / .shop-link.
    contextButtonBackground: template.card_style,
  });

  // Match Banner + Highlight border token (tertiary / button text)
  const borderColor =
    template.button?.text || template.button?.border || template.text || '#e6e7eb';

  return {
    cardBackground: isDefault ? '#ffffff' : 'transparent',
    cardBorder: isDefault ? '#e6e7eb' : borderColor,
    fullViewBackground: isDefault ? '#ffffff' : 'transparent',
    fullViewBorder: isDefault ? '#e6e7eb' : borderColor,
    textColor: contentTextColor,
    mutedTextColor: isDefault ? '#6b7280' : contentTextColor,
    buttonBackground: shopParity.backgroundColor,
    buttonText: shopParity.color,
    priceColor: isDefault ? textColor : template.button?.background || textColor,
    inputBackground: isDefault ? '#f3f4f6' : 'rgba(255, 255, 255, 0.14)',
    inputTextColor: isDefault ? '#9ca3af' : textColor,
  };
}
